import type { Rng } from '../../core/rng';

/**
 * Pure music theory for the radio's generated songs: keys and modes, chord qualities, progression
 * templates per style, nearest-voicing voice leading, and phrase (melody) generation. No Web Audio.
 */

export type Mode = 'major' | 'minor' | 'dorian' | 'mixolydian';

export const SCALES: Record<Mode, readonly number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
};

export type Quality = 'maj' | 'min' | 'dom7' | 'maj7' | 'min7' | 'min9' | 'dom9' | 'maj6' | 'min6' | 'sus4' | 'dim7' | 'm7b5' | 'add9' | 'maj9' | 'dom7sus';

export const QUALITY_INTERVALS: Record<Quality, readonly number[]> = {
  maj: [0, 4, 7],
  min: [0, 3, 7],
  dom7: [0, 4, 7, 10],
  maj7: [0, 4, 7, 11],
  min7: [0, 3, 7, 10],
  min9: [0, 3, 7, 10, 14],
  dom9: [0, 4, 7, 10, 14],
  maj6: [0, 4, 7, 9],
  min6: [0, 3, 7, 9],
  sus4: [0, 5, 7],
  dim7: [0, 3, 6, 9],
  m7b5: [0, 3, 6, 10],
  add9: [0, 4, 7, 14],
  maj9: [0, 4, 7, 11, 14],
  dom7sus: [0, 5, 7, 10],
};

const QUALITY_SUFFIX: Record<Quality, string> = {
  maj: '',
  min: 'm',
  dom7: '7',
  maj7: 'maj7',
  min7: 'm7',
  min9: 'm9',
  dom9: '9',
  maj6: '6',
  min6: 'm6',
  sus4: 'sus4',
  dim7: 'dim7',
  m7b5: 'm7b5',
  add9: 'add9',
  maj9: 'maj9',
  dom7sus: '7sus4',
};

const PC_NAMES = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

export interface Chord {
  /** Pitch class of the root, 0..11 (C = 0). */
  pc: number;
  quality: Quality;
  /** Semitones of the root above the song tonic (for analysis / tests). */
  st: number;
  name: string;
}

/** Intervals of a chord (semitones above the root). */
export function chordTones(c: Chord): readonly number[] {
  return QUALITY_INTERVALS[c.quality];
}

/** True when `midi` is a chord tone (any octave). */
export function isChordTone(c: Chord, midi: number): boolean {
  const rel = (((midi - c.pc) % 12) + 12) % 12;
  return chordTones(c).some((i) => i % 12 === rel);
}

export function pcName(pc: number): string {
  return PC_NAMES[((pc % 12) + 12) % 12] as string;
}

export function makeChord(tonic: number, st: number, quality: Quality): Chord {
  const pc = (((tonic + st) % 12) + 12) % 12;
  return { pc, quality, st: ((st % 12) + 12) % 12, name: pcName(pc) + QUALITY_SUFFIX[quality] };
}

/** One bar of harmony: one chord, or two (first half / second half). */
export type BarSpec = readonly (readonly [number, Quality])[];

/**
 * Progression templates per style as [semitones above tonic, quality] per bar (two entries = a split
 * bar). Templates are 4, 8 or 12 bars and repeat to fill a section.
 */
export const PROGRESSIONS: Record<string, readonly (readonly BarSpec[])[]> = {
  soulMajor: [
    [[[0, 'maj']], [[9, 'min7']], [[5, 'maj7']], [[7, 'dom7']]],
    [[[0, 'maj']], [[5, 'maj7']], [[0, 'maj']], [[7, 'dom7']]],
    [[[2, 'min7']], [[7, 'dom7']], [[0, 'maj7']], [[9, 'min7']]],
    [[[0, 'maj']], [[4, 'min7']], [[5, 'maj7']], [[5, 'min6']]],
    [[[0, 'maj']], [[10, 'maj']], [[5, 'maj']], [[0, 'maj']]],
    [[[5, 'maj7']], [[7, 'dom7']], [[4, 'min7']], [[9, 'min7']]],
    [[[0, 'maj7'], [9, 'min7']], [[2, 'min7'], [7, 'dom7']], [[0, 'maj7'], [9, 'min7']], [[2, 'min7'], [7, 'dom7']]],
  ],
  soulMinor: [
    [[[0, 'min7']], [[5, 'min7']], [[8, 'maj7']], [[7, 'dom7']]],
    [[[0, 'min9']], [[10, 'maj']], [[8, 'maj']], [[7, 'dom7']]],
    [[[0, 'min7']], [[2, 'm7b5']], [[7, 'dom7']], [[0, 'min7']]],
    [[[0, 'min7']], [[3, 'maj7']], [[5, 'min7']], [[7, 'dom7sus'], [7, 'dom7']]],
  ],
  beatMajor: [
    [[[0, 'maj']], [[5, 'maj']], [[7, 'maj']], [[5, 'maj']]],
    [[[0, 'maj']], [[10, 'maj']], [[5, 'maj']], [[0, 'maj']]],
    [[[0, 'maj']], [[3, 'maj']], [[5, 'maj']], [[0, 'maj']]],
    [[[0, 'maj']], [[9, 'min']], [[2, 'min']], [[7, 'dom7']]],
    [[[0, 'dom7']], [[0, 'dom7']], [[0, 'dom7']], [[0, 'dom7']], [[5, 'dom7']], [[5, 'dom7']], [[0, 'dom7']], [[0, 'dom7']], [[7, 'dom7']], [[5, 'dom7']], [[0, 'dom7']], [[7, 'dom7']]],
    [[[0, 'maj']], [[0, 'maj']], [[5, 'maj']], [[7, 'maj']]],
  ],
  beatMinor: [
    [[[0, 'min']], [[8, 'maj']], [[10, 'maj']], [[0, 'min']]],
    [[[0, 'min']], [[3, 'maj']], [[10, 'maj']], [[7, 'maj']]],
    [[[0, 'min7']], [[5, 'min7']], [[0, 'min7']], [[7, 'dom7']]],
  ],
  easyMajor: [
    [[[0, 'maj7']], [[9, 'min7']], [[2, 'min7']], [[7, 'dom7']]],
    [[[0, 'maj7']], [[5, 'maj7']], [[4, 'min7']], [[9, 'dom7']], [[2, 'min7']], [[7, 'dom7']], [[0, 'maj7']], [[7, 'dom7sus']]],
    [[[2, 'min7']], [[7, 'dom9']], [[0, 'maj9']], [[0, 'maj6']]],
    [[[0, 'maj7']], [[0, 'dom7']], [[5, 'maj7']], [[5, 'min6']], [[0, 'maj7']], [[9, 'min7']], [[2, 'min7']], [[7, 'dom7']]],
    [[[0, 'maj9']], [[4, 'min7']], [[5, 'maj7']], [[7, 'dom9']]],
  ],
  easyMinor: [
    [[[0, 'min9']], [[5, 'min7']], [[2, 'm7b5']], [[7, 'dom7']]],
    [[[0, 'min7']], [[8, 'maj7']], [[10, 'dom7']], [[3, 'maj7']], [[8, 'maj7']], [[2, 'm7b5']], [[7, 'dom7']], [[0, 'min7']]],
  ],
  skaMajor: [
    [[[0, 'maj']], [[5, 'maj']], [[7, 'maj']], [[5, 'maj']]],
    [[[0, 'maj']], [[7, 'maj']], [[0, 'maj']], [[7, 'maj']]],
    [[[0, 'maj']], [[9, 'min']], [[5, 'maj']], [[7, 'maj']]],
    [[[0, 'maj6']], [[5, 'maj']], [[0, 'maj6']], [[7, 'dom7']]],
    [[[0, 'maj']], [[0, 'maj']], [[5, 'maj']], [[5, 'maj']], [[0, 'maj']], [[0, 'maj']], [[7, 'dom7']], [[7, 'dom7']]],
  ],
  skaMinor: [
    [[[0, 'min']], [[10, 'maj']], [[8, 'maj']], [[10, 'maj']]],
    [[[0, 'min']], [[5, 'min']], [[0, 'min']], [[7, 'dom7']]],
    [[[0, 'min7']], [[3, 'maj']], [[5, 'min7']], [[7, 'maj']]],
  ],
  // Bridges start away from home and end on V7 (a pull back into the final chorus).
  bridgeMajor: [
    [[[5, 'maj']], [[5, 'min']], [[0, 'maj']], [[7, 'dom7']]],
    [[[9, 'min7']], [[2, 'min7']], [[7, 'dom7']], [[0, 'maj7']], [[9, 'min7']], [[2, 'min7']], [[7, 'dom7']], [[7, 'dom7']]],
    [[[8, 'maj']], [[10, 'maj']], [[0, 'maj']], [[0, 'maj']], [[8, 'maj']], [[10, 'maj']], [[7, 'dom7']], [[7, 'dom7']]],
    [[[5, 'maj7']], [[4, 'min7']], [[2, 'min7']], [[7, 'dom7']]],
  ],
  bridgeMinor: [
    [[[8, 'maj7']], [[10, 'dom7']], [[0, 'min7']], [[7, 'dom7']]],
    [[[5, 'min7']], [[0, 'min7']], [[5, 'min7']], [[7, 'dom7']]],
    [[[3, 'maj7']], [[8, 'maj7']], [[2, 'm7b5']], [[7, 'dom7']]],
  ],
};

/** Resolve a template (repeated to `bars`) into chords in `tonic`, each bar an array of 1-2 chords. */
export function resolveProgression(template: readonly BarSpec[], tonic: number, bars: number): Chord[][] {
  const out: Chord[][] = [];
  for (let b = 0; b < bars; b++) {
    const spec = template[b % template.length] as BarSpec;
    out.push(spec.map(([st, q]) => makeChord(tonic, st, q)));
  }
  return out;
}

/** Fold a MIDI note into [lo, hi] by octaves. */
export function fitRange(midi: number, lo: number, hi: number): number {
  let m = midi;
  while (m < lo) m += 12;
  while (m > hi) m -= 12;
  return m;
}

/**
 * Chord tones used for a comp voicing (root dropped from 4+ note chords: the bass has it). Returned as
 * pitch classes.
 */
export function compTones(c: Chord): number[] {
  const iv = chordTones(c);
  const use = iv.length >= 4 ? iv.slice(1) : iv;
  return use.map((i) => (((c.pc + i) % 12) + 12) % 12);
}

/**
 * Nearest-voicing voice leading: every placement of the chord's comp tones inside [lo, hi] (each pitch
 * class once, span <= 15 semitones) is scored by how far the voices move from `prev`; the cheapest wins.
 * Without a previous voicing the most compact placement around the middle of the range is used.
 */
export function leadVoicing(c: Chord, prev: readonly number[] | null, lo = 55, hi = 79): number[] {
  const pcs = compTones(c);
  const options = pcs.map((pc) => {
    const list: number[] = [];
    for (let m = lo; m <= hi; m++) if ((((m - pc) % 12) + 12) % 12 === 0) list.push(m);
    return list;
  });
  let best: number[] | null = null;
  let bestCost = Infinity;
  const mid = (lo + hi) / 2;
  const walk = (i: number, acc: number[]): void => {
    if (i === options.length) {
      const v = acc.slice().sort((a, b) => a - b);
      if ((v[v.length - 1] as number) - (v[0] as number) > 15) return;
      let cost = 0;
      if (prev && prev.length) {
        // Match each voice to the nearest previous voice (sorted lists: index-wise after padding).
        for (let k = 0; k < v.length; k++) {
          let d = Infinity;
          for (const p of prev) d = Math.min(d, Math.abs((v[k] as number) - p));
          cost += d;
        }
        // Discourage parallel octave leaps of the whole voicing.
        const centre = v.reduce((s, x) => s + x, 0) / v.length;
        const pc = prev.reduce((s, x) => s + x, 0) / prev.length;
        cost += Math.abs(centre - pc) * 0.25;
      } else {
        for (const m of v) cost += Math.abs(m - mid);
      }
      if (cost < bestCost) {
        bestCost = cost;
        best = v;
      }
      return;
    }
    for (const m of options[i] as number[]) walk(i + 1, [...acc, m]);
  };
  walk(0, []);
  return best ?? pcs.map((pc) => fitRange(pc + 60, lo, hi)).sort((a, b) => a - b);
}

/** Total voice movement between two voicings (semitones; nearest-neighbour), for tests/stats. */
export function voicingDistance(a: readonly number[], b: readonly number[]): number {
  let d = 0;
  for (const x of b) {
    let m = Infinity;
    for (const y of a) m = Math.min(m, Math.abs(x - y));
    d += m;
  }
  return d;
}

// ---------------------------------------------------------------------------------------------
// Melody

export interface PhraseNote {
  /** Sixteenth step from the phrase start. */
  step: number;
  midi: number;
  /** Steps. */
  dur: number;
  /** 0..1 accent. */
  acc: number;
}

export interface PhraseOpts {
  rng: Rng;
  /** Scale pitch classes relative to the tonic. */
  scale: readonly number[];
  tonic: number;
  /** Harmony per bar of the phrase (1-2 chords per bar). */
  chords: readonly (readonly Chord[])[];
  /** One-bar onset templates (steps 0..15). */
  rhythms: readonly (readonly number[])[];
  lo: number;
  hi: number;
  /** Starting pitch (midi) if any: the phrase begins near it. */
  from?: number;
  /** 0..1 how much the phrase likes to leap (otherwise stepwise). */
  leap?: number;
  /** Staccato chance 0..1. */
  stacc?: number;
  /** End the phrase on the tonic triad (a cadence). */
  cadence?: boolean;
}

function scaleNotes(scale: readonly number[], tonic: number, lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let m = lo; m <= hi; m++) if (scale.includes((((m - tonic) % 12) + 12) % 12)) out.push(m);
  return out;
}

/** Chord at a step of a bar (split bars switch at step 8). */
export function chordAtStep(bar: readonly Chord[], step: number): Chord {
  if (bar.length > 1 && step >= 8) return bar[1] as Chord;
  return bar[0] as Chord;
}

/**
 * A phrase over `chords.length` bars: a one-bar rhythm template per bar (the same one for bars 1 and 3,
 * so the phrase has an internal motif), chord tones on the strong steps, scale steps between, an
 * arch contour (rising in the first half, settling in the second), stepwise by default.
 */
export function generatePhrase(o: PhraseOpts): PhraseNote[] {
  const rng = o.rng;
  const bars = o.chords.length;
  const pool = scaleNotes(o.scale, o.tonic, o.lo, o.hi);
  if (pool.length === 0) return [];
  const leap = o.leap ?? 0.2;
  const stacc = o.stacc ?? 0.3;
  const rhythmA = rng.pick(o.rhythms);
  const rhythmB = rng.chance(0.5) ? rhythmA : rng.pick(o.rhythms);
  const notes: PhraseNote[] = [];
  const mid = (o.lo + o.hi) / 2;
  let cur = o.from ?? pool.reduce((best, m) => (Math.abs(m - mid) < Math.abs(best - mid) ? m : best), pool[0] as number);
  let idx = pool.indexOf(fitRange(cur, o.lo, o.hi));
  if (idx < 0) idx = Math.floor(pool.length / 2);
  const onsets: { step: number; strong: boolean }[] = [];
  for (let b = 0; b < bars; b++) {
    const r = b % 2 === 0 ? rhythmA : rhythmB;
    for (const s of r) onsets.push({ step: b * 16 + s, strong: s % 8 === 0 || (s % 4 === 0 && rng.chance(0.6)) });
  }
  // Drop a few onsets at random so bars breathe (never the first).
  const kept = onsets.filter((_, i) => i === 0 || rng.chance(0.9));
  for (let i = 0; i < kept.length; i++) {
    const on = kept[i] as { step: number; strong: boolean };
    const next = kept[i + 1]?.step ?? bars * 16;
    const bar = o.chords[Math.floor(on.step / 16)] as readonly Chord[];
    const chord = chordAtStep(bar, on.step % 16);
    const progress = on.step / (bars * 16);
    // Contour: climb through the first ~40 %, fall back after ~65 %.
    const drift = progress < 0.4 ? 0.25 : progress > 0.65 ? -0.3 : 0;
    const last = i === kept.length - 1;
    const prevPitch = cur;
    let maxDur = 16;
    if (last && (o.cadence ?? true)) {
      // Resolve: nearest tonic / third / fifth to the current pitch, preferring the tonic.
      const targets = [0, 4, 7, 3].map((iv) => ((o.tonic + iv) % 12 + 12) % 12).filter((pc) => o.scale.includes(((pc - o.tonic) % 12 + 12) % 12));
      let best = cur;
      let bd = Infinity;
      for (const m of pool) {
        if (!targets.includes(((m % 12) + 12) % 12)) continue;
        const d = Math.abs(m - cur) + (((m - o.tonic) % 12 + 12) % 12 === 0 ? 0 : 1.5);
        if (d < bd) {
          bd = d;
          best = m;
        }
      }
      cur = best;
    } else if (on.strong || i === 0) {
      // A chord tone within a fourth (a fifth if need be), biased by the contour; the opening note
      // is the chord tone nearest the starting pitch.
      if (i === 0) {
        if (!isChordTone(chord, cur)) {
          let best = cur;
          let bd = Infinity;
          for (const m of pool) {
            if (!isChordTone(chord, m)) continue;
            const d = Math.abs(m - cur);
            if (d < bd) {
              bd = d;
              best = m;
            }
          }
          cur = best;
        }
      } else {
        let cands = pool.filter((m) => isChordTone(chord, m) && Math.abs(m - cur) <= 5 && m !== cur);
        if (!cands.length) cands = pool.filter((m) => isChordTone(chord, m) && Math.abs(m - cur) <= 7);
        if (cands.length) {
          // Mostly by step: a chord tone a tone away is favoured over a third, a fourth is rare.
          const w = cands.map((m) => {
            const d = Math.abs(m - cur);
            return (d <= 2 ? 3 : d <= 4 ? 1 : 0.4) + (Math.sign(m - cur) === Math.sign(drift) ? 1.2 : 0);
          });
          cur = rng.weighted(cands, w);
        }
      }
    } else if (i > 0) {
      const step = rng.chance(leap) ? rng.pick([-3, -2, 2, 3]) : rng.pick(drift > 0 ? [1, 1, 1, -1, 2] : drift < 0 ? [-1, -1, -1, 1, -2] : [1, -1, 1, -1, 0, 2, -2]);
      idx = Math.max(0, Math.min(pool.length - 1, pool.indexOf(cur) + step));
      cur = pool[idx] as number;
      // A passing note may rub against the chord for a step; a HELD weak-beat note must not sit a
      // semitone above a chord tone (the 4th over a major third, a b9 over the root: the avoid notes).
      // Move it one scale step onto a chord tone (the one nearer the previous pitch, so the line stays
      // stepwise); if neither neighbour is one, keep the pitch but make it a short passing note.
      if (next - on.step >= 3 && !isChordTone(chord, cur) && isChordTone(chord, cur - 1)) {
        const alts = [pool[idx - 1], pool[idx + 1]].filter((m): m is number => m !== undefined && isChordTone(chord, m));
        if (alts.length) {
          const alt = alts.reduce((a, b) => (Math.abs(b - prevPitch) < Math.abs(a - prevPitch) ? b : a));
          idx = pool.indexOf(alt);
          cur = alt;
        } else maxDur = 2;
      }
    }
    const gap = next - on.step;
    const dur = Math.min(maxDur, Math.max(1, rng.chance(stacc) ? Math.min(gap, Math.max(1, Math.round(gap * 0.5))) : gap - (gap > 2 && rng.chance(0.3) ? 1 : 0)));
    notes.push({ step: on.step, midi: cur, dur, acc: on.step % 16 === 0 ? 1 : on.strong ? 0.8 : 0.6 });
  }
  return notes;
}

/** The phrase with a new ending: the last `n` notes re-resolved to a different chord tone. */
export function varyEnding(phrase: readonly PhraseNote[], rng: Rng, chords: readonly (readonly Chord[])[], tonic: number, up: boolean): PhraseNote[] {
  const out = phrase.map((n) => ({ ...n }));
  const n = Math.min(2, out.length);
  for (let i = out.length - n; i < out.length; i++) {
    const note = out[i] as PhraseNote;
    const bar = chords[Math.floor(note.step / 16)] as readonly Chord[];
    const chord = chordAtStep(bar, note.step % 16);
    const dir = up ? 1 : -1;
    for (let d = 1; d <= 7; d++) {
      const m = note.midi + dir * d;
      if (isChordTone(chord, m)) {
        note.midi = m;
        break;
      }
    }
    if (i === out.length - 1 && rng.chance(0.5)) note.dur = Math.max(2, note.dur + 2);
  }
  void tonic;
  return out;
}

/**
 * Re-fit a phrase to other harmony, as a motif is answered over the next chords: a strong-beat note
 * that is not a chord tone moves to the nearest scale chord tone within a major third, a held
 * weak-beat avoid note (a semitone above a chord tone) steps onto one. Rhythm and contour are kept.
 */
export function fitPhrase(phrase: readonly PhraseNote[], chords: readonly (readonly Chord[])[], scale: readonly number[], tonic: number, lo: number, hi: number, keepLast = false): PhraseNote[] {
  const pool = scaleNotes(scale, tonic, lo, hi);
  return phrase.map((n, i) => {
    const bar = chords[Math.floor(n.step / 16)];
    // The cadence note of a resolving phrase stays where the composer put it (the tonic triad).
    if (!bar || (keepLast && i === phrase.length - 1)) return { ...n };
    const chord = chordAtStep(bar, n.step % 16);
    if (isChordTone(chord, n.midi)) return { ...n };
    const strong = n.step % 8 === 0;
    const avoid = n.dur >= 3 && isChordTone(chord, n.midi - 1);
    if (!strong && !avoid) return { ...n };
    let best: number | null = null;
    for (const m of pool) {
      const d = Math.abs(m - n.midi);
      if (d > 4 || !isChordTone(chord, m)) continue;
      if (best === null || d < Math.abs(best - n.midi) || (d === Math.abs(best - n.midi) && m < best)) best = m;
    }
    return best === null ? { ...n } : { ...n, midi: best };
  });
}

/** Transpose a phrase. */
export function transposePhrase(phrase: readonly PhraseNote[], semis: number, lo: number, hi: number): PhraseNote[] {
  return phrase.map((n) => ({ ...n, midi: fitRange(n.midi + semis, lo, hi) }));
}

/** Pitch-class histogram entropy (bits) of a note list: 0 = one pitch, ~3.3 = all twelve equal. */
export function pitchClassEntropy(midis: readonly number[]): number {
  const h = new Array<number>(12).fill(0);
  for (const m of midis) h[((m % 12) + 12) % 12]++;
  const n = midis.length;
  if (!n) return 0;
  let e = 0;
  for (const c of h) {
    if (!c) continue;
    const p = c / n;
    e -= p * Math.log2(p);
  }
  return e;
}
